
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/Button';

interface OpsEndpointCardProps {
    title: string;
    endpoint: string;
    lastUpdated?: number; // Unix timestamp
    onRefresh: () => void;
    isLoading: boolean;
    error?: string | null;
    children: React.ReactNode;
}

export const OpsEndpointCard: React.FC<OpsEndpointCardProps> = ({
    title,
    endpoint,
    lastUpdated,
    onRefresh,
    isLoading,
    error,
    children
}) => {
    return (
        <Card className="w-full h-full flex flex-col">
            <CardHeader className="flex flex-row items-center justify-between pb-2 bg-muted/20">
                <div className="flex flex-col gap-1">
                    <CardTitle className="text-lg font-medium">{title}</CardTitle>
                    <div className="text-xs text-muted-foreground font-mono bg-muted px-2 py-0.5 rounded w-fit">
                        GET {endpoint}
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    {error === "Unauthorized" ? (
                        <span className="text-xs text-yellow-500 font-medium px-2 py-1 bg-yellow-500/10 rounded">
                            Unauthorized
                        </span>
                    ) : error && (
                        <span className="text-xs text-red-500 font-medium px-2 py-1 bg-red-500/10 rounded">
                            Error
                        </span>
                    )}
                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={onRefresh}
                        disabled={isLoading}
                        className="h-8 w-8"
                        title="Refresh Data"
                    >
                        <RefreshCw className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
                    </Button>
                </div>
            </CardHeader>
            <CardContent className="flex-1 p-6 overflow-auto">
                {children}
            </CardContent>
            {lastUpdated && (
                <div className="px-6 py-2 bg-muted/10 border-t text-xs text-muted-foreground flex justify-between">
                    <span>Source: {endpoint}</span>
                    <span>Last Updated: {new Date(lastUpdated * 1000).toISOString()}</span>
                </div>
            )}
        </Card>
    );
};
